import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { keys, portalQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDateTime, investmentBaselineSchema } from '@blimo/shared';
import { baselineToForm, formToBaselinePayload } from '../lib/baseline.js';
import { Alert, AlertDescription, applyProblemToForm, Button, ErrorState, Form, FormRootError, InputField, Skeleton, toast } from '@blimo/ui';

const FIELDS = ['purchase_price', 'initial_rent', 'initial_rental_income', 'initial_costs', 'purchase_month'];

/**
 * Investitions-Startwerte einer Liegenschaft (finance.baselines.manage). Every save creates a new version.
 * @param {{ propertyId: string }} props
 */
export function InvestmentBaselineSettings({ propertyId }) {
  const api = useApi();
  const query = useQuery(portalQueries.investmentBaseline(api, propertyId));
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.isPending) return <Skeleton className="h-72 w-full max-w-xl" />;
  // key: re-initialise the form when another version arrives
  return <BaselineForm key={query.data?.updated_at ?? 'new'} propertyId={propertyId} baseline={query.data} />;
}

function BaselineForm({ propertyId, baseline }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const form = useForm({ resolver: zodResolver(investmentBaselineSchema), defaultValues: baselineToForm(baseline) });
  const mutation = useMutation({
    mutationFn: (values) => api.portal.properties.updateInvestmentBaseline(propertyId, formToBaselinePayload(values)),
    onSuccess: (saved) => {
      queryClient.setQueryData(portalQueries.investmentBaseline(api, propertyId).queryKey, saved);
      // yields, deviations and the accumulated view depend on the baseline
      queryClient.invalidateQueries({ queryKey: keys.portal.finance() });
      toast.success('Startwerte gespeichert');
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync(values);
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: FIELDS });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex max-w-xl flex-col gap-4" noValidate aria-label="Investitions-Startwerte">
        <Alert variant="info">
          <History aria-hidden="true" />
          <AlertDescription>
            Änderungen werden versioniert gespeichert.
            {baseline?.updated_at ? ` Aktueller Stand vom ${formatDateTime(baseline.updated_at)}.` : ' Bisher sind keine Startwerte hinterlegt.'}
          </AlertDescription>
        </Alert>
        <FormRootError />
        <div className="grid gap-4 rounded-xl border bg-card p-4 sm:grid-cols-2">
          <InputField name="purchase_price" label="Kaufpreis" inputMode="decimal" autoComplete="off" description="in Euro, z. B. 250.000,00" />
          <InputField name="purchase_month" label="Kaufdatum (Monat)" type="month" placeholder="JJJJ-MM" description="Grundlage der kumulierten Rendite" />
          <InputField
            name="initial_rent"
            label="Startwert Rendite (Jahresmiete beim Kauf)"
            inputMode="decimal"
            autoComplete="off"
            description="Jahresmiete zum Kaufzeitpunkt in Euro"
          />
          <InputField name="initial_rental_income" label="Mieteinnahmen beim Kauf" inputMode="decimal" autoComplete="off" description="in Euro" />
          <InputField
            name="initial_costs"
            label="Kosten beim Kauf"
            inputMode="decimal"
            autoComplete="off"
            description="in Euro, z. B. Nebenkosten des Erwerbs"
          />
        </div>
        <Button type="submit" className="w-full sm:w-fit" loading={form.formState.isSubmitting}>
          Speichern
        </Button>
      </form>
    </Form>
  );
}
